/**
 * Cognara chat scoped to one contract. The thread is persisted server-side so
 * the conversation is still there tomorrow.
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles, SendHorizonal, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { apiError } from './portfolioUi';
import ReactMarkdown from 'react-markdown';
import { Input } from '@/components/ui/input';

const SUGGESTIONS = [
  'What are my payment milestones and when do they trigger?',
  'What is the LD rate and cap, and what is my current exposure?',
  'Which obligations fall on us in the next 30 days?',
  'Where is this contract weakest commercially?',
];

export const CognaraContractChat = ({ contractId, contractNumber }) => {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/contracts/contracts/${contractId}/cognara-chat`)
      .then(({ data }) => { setMessages(data.messages || []); setSessionId(data.session_id); })
      .catch(() => {});
  }, [contractId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async (text) => {
    const message = (text ?? draft).trim();
    if (!message || busy) return;
    setDraft('');
    setMessages(m => [...m, { role: 'user', content: message, at: new Date().toISOString() }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/contracts/${contractId}/cognara-chat`,
        { message, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(apiError(e, 'Cognara could not answer that'));
      setMessages(m => m.slice(0, -1));
    } finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-xl flex flex-col h-[560px]" data-testid="cognara-contract-chat">
      <header className="px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: 'var(--border-subtle)' }}>
        <Sparkles className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} />
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Cognara AI</h3>
          <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            Reads the extracted clauses, obligations and audit for {contractNumber} · thread saved
          </p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {!messages.length && (
          <div data-testid="chat-suggestions">
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Ask anything about this contract. For example:</p>
            <div className="flex flex-col gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)}
                        className="text-left text-xs rounded-lg border px-3 py-2"
                        style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                        data-testid={`chat-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
               data-testid={`chat-message-${m.role}-${i}`}>
              <div className="max-w-[80%] rounded-2xl px-4 py-3 text-sm"
                 style={m.role === 'user'
                   ? { background: 'var(--active-bg)', color: 'var(--text-primary)' }
                   : { background: 'var(--surface-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
              {m.role === 'assistant'
                ? <div className="cognara-md"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                : <span className="whitespace-pre-wrap">{m.content}</span>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }} data-testid="chat-thinking">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cognara is reading the contract…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="px-6 py-4 border-t flex items-center gap-2" style={{ borderColor: 'var(--border-subtle)' }}>
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && send()}
               placeholder="Ask Cognara about this contract…" className="h-10 text-sm"
               data-testid="chat-input" />
        <button onClick={() => send()} disabled={busy || !draft.trim()}
                className="rounded-full h-10 w-10 inline-flex items-center justify-center text-white disabled:opacity-40"
                style={{ background: 'var(--brand-accent)' }} data-testid="chat-send">
          <SendHorizonal className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CognaraContractChat;
